import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { describeRequest } from './TimeClockDialogs';
import {
    addDays, dayLabel, hm, hmSigned, isoDate, isoWeek, mondayOf, stateLabel, stateTone, surface, time, tone, typeLabel, weekdayShort,
} from './tcFormat';

/**
 * Overview: the four questions HR asks — who is in, how many hours, what is
 * wrong, and (in the header) where is the export.
 */
const TimeClockOverviewTab = ({ data, t, lang, theme, storeName, weekFrom, setWeekFrom, onDay, onCorrect, onDecide }) => {
    const s = surface(theme);
    const week = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekFrom, i)), [weekFrom]);
    const today = isoDate(new Date());

    // --- week grid -----------------------------------------------------------
    const grid = useMemo(() => {
        const byUser = {};
        (data.week || []).forEach((day) => {
            if (!byUser[day.user_id]) byUser[day.user_id] = { id: day.user_id, name: day.user_name, days: {} };
            byUser[day.user_id].days[day.work_date] = day;
        });
        // Active employees with contract hours show up even without stamps:
        // a week at 0 against 38 hours is worth seeing.
        (data.employees || []).forEach((e) => {
            if (e.is_active && e.contract_minutes_week && !byUser[e.id]) byUser[e.id] = { id: e.id, name: e.name, days: {} };
        });
        const contracts = {};
        (data.employees || []).forEach((e) => { contracts[e.id] = e.contract_minutes_week; });
        return Object.values(byUser).map((row) => {
            const total = Object.values(row.days).reduce((sum, d) => sum + (d.worked_minutes || 0), 0);
            const contract = contracts[row.id] ?? null;
            return { ...row, total, contract, diff: contract === null ? null : total - contract };
        }).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, [data]);

    const anyContract = grid.some((row) => row.contract !== null);
    const weekTotal = grid.reduce((sum, row) => sum + row.total, 0);
    const thisWeek = weekFrom === mondayOf(today);

    // --- attention list ------------------------------------------------------
    const pending = data.pending || [];
    const incomplete = data.incomplete || [];
    const present = data.present || [];
    const longShifts = (data.week || []).filter((d) => (d.anomaly_flags || []).includes('LONG_SHIFT'));
    // Own-phone stamps that may have been outside the store radius.
    const locationDoubts = (data.week || []).filter((d) => (d.anomaly_flags || []).includes('LOCATION_DOUBT'));
    const attentionCount = pending.length + incomplete.length + longShifts.length + locationDoubts.length;

    const kpis = [
        { label: t('tc_kpi_present', 'Present now'), value: present.length },
        { label: thisWeek ? t('tc_kpi_hours_week', 'Hours this week') : t('tc_week_cap', 'Week') + ' ' + isoWeek(weekFrom), value: hm(weekTotal, lang) },
        { label: t('tc_kpi_incomplete', 'Incomplete days'), value: incomplete.length, alert: incomplete.length > 0, hint: t('tc_last_31_days', 'last 31 days') },
        { label: t('tc_kpi_pending', 'Requests waiting'), value: pending.length, alert: pending.length > 0 },
    ];

    const tag = (label, color) => (
        <Box component="span" sx={{
            display: 'inline-block', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
            color, backgroundColor: color + (s.dark ? '26' : '14'), borderRadius: '4px', px: 0.75, py: 0.25,
            whiteSpace: 'nowrap', lineHeight: 1.5,
        }}>{label}</Box>
    );

    const sectionHead = (text, count, right) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, minHeight: 48, borderBottom: s.line }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{text}</Typography>
            {count !== undefined && (
                <Box component="span" sx={{
                    fontSize: 12, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: 'text.secondary',
                    backgroundColor: s.hover, borderRadius: '10px', px: 0.9, lineHeight: '20px',
                }}>{count}</Box>
            )}
            <Box sx={{ flex: 1 }} />
            {right}
        </Box>
    );

    // One line in "To handle": tag | text | actions, aligned across rows.
    const attentionRow = (key, tagNode, text, actions) => (
        <Box key={key} sx={{
            display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '104px 1fr auto' }, alignItems: 'center',
            columnGap: 1.5, rowGap: 0.75, px: 2, py: 1.1, '&:not(:last-of-type)': { borderBottom: s.line },
        }}>
            <Box>{tagNode}</Box>
            <Typography sx={{ fontSize: 13.5, lineHeight: 1.45 }}>{text}</Typography>
            <Box sx={{ display: 'flex', gap: 0.75, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>{actions}</Box>
        </Box>
    );

    const incompleteText = (day) => {
        const flags = day.anomaly_flags || [];
        const parts = [];
        if (day.first_in) parts.push(typeLabel(t, 'IN') + ' ' + time(day.first_in));
        if (flags.includes('MISSING_OUT')) parts.push(t('tc_no_out', 'no OUT'));
        if (flags.includes('MISSING_IN')) parts.push(t('tc_no_in', 'no IN') + (day.last_out ? ' · ' + typeLabel(t, 'OUT') + ' ' + time(day.last_out) : ''));
        if (flags.includes('PENDING_CORRECTION')) parts.push(t('tc_request_waiting', 'request waiting'));
        return parts.join(', ');
    };

    const dayFor = (userId, userName, date) =>
        (data.week || []).find((d) => d.user_id === userId && d.work_date === date)
        || { user_id: userId, user_name: userName, work_date: date, worked_minutes: 0, break_minutes: 0, anomaly_flags: [] };

    const small = { textTransform: 'none', minWidth: 0, px: 1.25, py: 0.25, fontSize: 13 };
    const cellPad = { px: 1.25, py: 1 };
    const todayBg = s.dark ? 'rgba(0,113,187,0.12)' : 'rgba(0,113,187,0.05)';

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Key figures */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }, gap: 1.5 }}>
                {kpis.map((kpi) => (
                    <Box key={kpi.label} sx={{ ...s.card, px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                        <Typography noWrap sx={{ fontSize: 11.5, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>{kpi.label}</Typography>
                        <Typography sx={{ fontSize: 28, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: kpi.alert ? tone.bad : 'text.primary', lineHeight: 1.2 }}>
                            {kpi.value}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: 'text.secondary', minHeight: 18 }}>{kpi.hint || ''}</Typography>
                    </Box>
                ))}
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 3fr) minmax(0, 2fr)' }, gap: 2, alignItems: 'start' }}>
                {/* To handle */}
                <Box sx={{ ...s.card, overflow: 'hidden' }}>
                    {sectionHead(t('tc_pending_title', 'To handle'), attentionCount)}
                    {attentionCount === 0 && (
                        <Typography sx={{ px: 2, py: 2, fontSize: 14, color: 'text.secondary' }}>{t('tc_nothing_to_handle', 'Nothing to handle. Every day is complete.')}</Typography>
                    )}
                    <Box>
                        {pending.map((request) => attentionRow('p' + request.id,
                            tag(request.correction ? t('tc_tag_correction', 'Correction') : t('tc_tag_late', 'Late'), tone.warn),
                            describeRequest(t, lang, request),
                            <React.Fragment>
                                <Button size="small" color="error" sx={small} onClick={() => onDecide(request, false)}>{t('tc_reject', 'Reject')}</Button>
                                <Button size="small" variant="contained" disableElevation sx={small} onClick={() => onDecide(request, true)}>{t('tc_approve', 'Approve')}</Button>
                            </React.Fragment>
                        ))}
                        {incomplete.map((day) => {
                            const missing = (day.anomaly_flags || []).includes('MISSING_IN') ? 'IN' : 'OUT';
                            const waiting = (day.anomaly_flags || []).includes('PENDING_CORRECTION');
                            return attentionRow('i' + day.user_id + day.work_date,
                                tag(t('tc_status_incomplete', 'Incomplete'), tone.bad),
                                <React.Fragment><b>{day.user_name}</b> · {dayLabel(day.work_date, lang)} · {incompleteText(day)}</React.Fragment>,
                                <React.Fragment>
                                    <Button size="small" sx={small} onClick={() => onDay(day)}>{t('tc_view', 'View')}</Button>
                                    {!waiting && (
                                        <Button size="small" variant="outlined" sx={small}
                                            onClick={() => onCorrect({ action: 'ADD', user_id: day.user_id, user_name: day.user_name, work_date: day.work_date, store_id: day.store_id, event_type: missing })}>
                                            {missing === 'IN' ? t('tc_add_in', 'Add IN') : t('tc_add_out', 'Add OUT')}
                                        </Button>
                                    )}
                                </React.Fragment>
                            );
                        })}
                        {longShifts.map((day) => attentionRow('l' + day.user_id + day.work_date,
                            tag(t('tc_tag_long', 'Long shift'), tone.warn),
                            <React.Fragment><b>{day.user_name}</b> · {dayLabel(day.work_date, lang)} · {time(day.first_in)}–{time(day.last_out)} · {hm(day.worked_minutes, lang)}</React.Fragment>,
                            <Button size="small" sx={small} onClick={() => onDay(day)}>{t('tc_view', 'View')}</Button>
                        ))}
                        {locationDoubts.map((day) => attentionRow('g' + day.user_id + day.work_date,
                            tag(t('tc_tag_location', 'Location'), tone.warn),
                            <React.Fragment><b>{day.user_name}</b> · {dayLabel(day.work_date, lang)} · {t('tc_location_doubt_text', 'stamped on the own phone, maybe outside the store radius')}</React.Fragment>,
                            <Button size="small" sx={small} onClick={() => onDay(day)}>{t('tc_view', 'View')}</Button>
                        ))}
                    </Box>
                </Box>

                {/* Present now */}
                <Box sx={{ ...s.card, overflow: 'hidden' }}>
                    {sectionHead(t('tc_present_title', 'Present now'), present.length)}
                    {present.length === 0 && (
                        <Typography sx={{ px: 2, py: 2, fontSize: 14, color: 'text.secondary' }}>{t('tc_present_empty', 'Nobody is clocked in.')}</Typography>
                    )}
                    <Box sx={{ maxHeight: 420, overflowY: 'auto' }}>
                        {present.map((person) => (
                            <Box key={person.user_id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1, '&:not(:last-of-type)': { borderBottom: s.line } }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: stateTone(person.state), flexShrink: 0 }} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontSize: 14, fontWeight: 600 }} noWrap>{person.user_name}</Typography>
                                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }} noWrap>{storeName(person.store_id)}</Typography>
                                </Box>
                                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                    <Typography sx={{ fontSize: 13, color: stateTone(person.state), fontWeight: 600 }}>
                                        {stateLabel(t, person.state)}
                                    </Typography>
                                    <Typography sx={{ fontSize: 12, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                                        {t('tc_since_short', 'since')} {time(person.since)}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>

            {/* Hours per employee in one week */}
            <Box sx={{ ...s.card, overflow: 'hidden' }}>
                {sectionHead(
                    <React.Fragment>
                        {t('tc_hours_per_employee', 'Hours per employee')} · {t('tc_week', 'week')} {isoWeek(weekFrom)}
                        <Typography component="span" sx={{ fontSize: 13, fontWeight: 400, color: 'text.secondary', ml: 1 }}>
                            {dayLabel(week[0], lang)} – {dayLabel(week[6], lang)}
                        </Typography>
                    </React.Fragment>,
                    undefined,
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                        <IconButton size="small" onClick={() => setWeekFrom(addDays(weekFrom, -7))} aria-label={t('tc_prev_week', 'Previous week')}><ChevronLeft fontSize="small" /></IconButton>
                        <Button size="small" sx={small} disabled={thisWeek} onClick={() => setWeekFrom(mondayOf(today))}>
                            {t('tc_this_week', 'This week')}
                        </Button>
                        <IconButton size="small" disabled={weekFrom >= mondayOf(today)} onClick={() => setWeekFrom(addDays(weekFrom, 7))} aria-label={t('tc_next_week', 'Next week')}><ChevronRight fontSize="small" /></IconButton>
                    </Box>
                )}
                <Box sx={{ overflowX: 'auto' }}>
                    <Box component="table" sx={{
                        width: '100%', borderCollapse: 'collapse', fontSize: 13.5, fontVariantNumeric: 'tabular-nums', minWidth: 760,
                        '& th': { fontSize: 11.5, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: s.line, whiteSpace: 'nowrap' },
                        '& tbody tr:not(:last-of-type)': { borderBottom: s.line },
                        '& tbody tr:hover': { backgroundColor: s.hover },
                    }}>
                        <thead>
                            <tr>
                                <Box component="th" sx={{ ...cellPad, pl: 2, textAlign: 'left' }}>{t('tc_employee', 'Employee')}</Box>
                                {week.map((date) => (
                                    <Box component="th" key={date} sx={{ ...cellPad, textAlign: 'right', color: date === today ? 'text.primary' : undefined, backgroundColor: date === today ? todayBg : undefined }}>
                                        {weekdayShort(date, lang)} {date.substring(8)}
                                    </Box>
                                ))}
                                <Box component="th" sx={{ ...cellPad, textAlign: 'right', color: 'text.primary' }}>{t('tc_total', 'Total')}</Box>
                                {anyContract && <Box component="th" sx={{ ...cellPad, textAlign: 'right' }}>{t('tc_contract', 'Contract')}</Box>}
                                {anyContract && <Box component="th" sx={{ ...cellPad, pr: 2, textAlign: 'right' }}>{t('tc_difference', 'Difference')}</Box>}
                            </tr>
                        </thead>
                        <tbody>
                            {grid.length === 0 && (
                                <tr><Box component="td" colSpan={11} sx={{ px: 2, py: 2.5, color: 'text.secondary' }}>{t('tc_no_days', 'No stamps in this period.')}</Box></tr>
                            )}
                            {grid.map((row) => (
                                <tr key={row.id}>
                                    <Box component="td" sx={{ ...cellPad, pl: 2, fontWeight: 600, whiteSpace: 'nowrap' }}>{row.name}</Box>
                                    {week.map((date) => {
                                        const day = row.days[date];
                                        const flags = day?.anomaly_flags || [];
                                        let marker = null;
                                        let color = 'inherit';
                                        let tip = '';
                                        if (day?.status === 'INCOMPLETE') { marker = '!'; color = tone.bad; tip = t('tc_status_incomplete', 'Incomplete'); }
                                        else if (flags.includes('LONG_SHIFT')) { marker = '⚠'; color = tone.warn; tip = t('tc_tag_long', 'Long shift'); }
                                        else if (day?.status === 'CORRECTED') { marker = '✎'; tip = t('tc_status_corrected', 'Corrected'); }
                                        else if (day?.status === 'OPEN') { tip = t('tc_status_open', 'Running'); }
                                        const future = date > today;
                                        const cellButton = {
                                            all: 'unset', cursor: 'pointer', borderRadius: '4px', px: 0.5, py: 0.25,
                                            '&:hover': { backgroundColor: s.hover },
                                            '&:focus-visible': { outline: '2px solid #0071BB' },
                                        };
                                        return (
                                            <Box component="td" key={date} sx={{ ...cellPad, px: 0.75, textAlign: 'right', color, backgroundColor: date === today ? todayBg : undefined }}>
                                                {day ? (
                                                    <Tooltip title={tip + (day.first_in ? (tip ? ' · ' : '') + time(day.first_in) + '–' + (time(day.last_out) || '?') : '')}>
                                                        <Box component="button" onClick={() => onDay(day)} sx={{ ...cellButton, color: day.status === 'OPEN' ? 'text.secondary' : 'inherit' }}>
                                                            {day.status === 'INCOMPLETE' && !day.worked_minutes ? '—' : hm(day.worked_minutes, lang)}
                                                            {marker && <Box component="span" sx={{ ml: 0.4, fontWeight: 700 }}>{marker}</Box>}
                                                        </Box>
                                                    </Tooltip>
                                                ) : (
                                                    !future && (
                                                        <Tooltip title={t('tc_add_stamp', 'Add missing stamp')}>
                                                            <Box component="button" onClick={() => onDay(dayFor(row.id, row.name, date))}
                                                                sx={{ ...cellButton, color: s.ghost, '&:hover': { backgroundColor: s.hover, color: 'text.secondary' } }}>
                                                                ·
                                                            </Box>
                                                        </Tooltip>
                                                    )
                                                )}
                                            </Box>
                                        );
                                    })}
                                    <Box component="td" sx={{ ...cellPad, textAlign: 'right', fontWeight: 700 }}>{hm(row.total, lang)}</Box>
                                    {anyContract && <Box component="td" sx={{ ...cellPad, textAlign: 'right', color: 'text.secondary' }}>{row.contract === null ? '' : hm(row.contract, lang)}</Box>}
                                    {anyContract && (
                                        <Box component="td" sx={{ ...cellPad, pr: 2, textAlign: 'right', fontWeight: 600, color: row.diff === null ? 'text.secondary' : row.diff < 0 ? tone.bad : row.diff > 0 ? tone.ok : 'inherit' }}>
                                            {row.diff === null ? '' : hmSigned(row.diff, lang)}
                                        </Box>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </Box>
                </Box>
                <Typography sx={{ px: 2, py: 1, fontSize: 12, color: 'text.secondary', borderTop: s.line }}>
                    <b>!</b> {t('tc_legend_incomplete', 'incomplete')} · ⚠ {t('tc_legend_long', 'longer than the maximum shift')} · ✎ {t('tc_legend_corrected', 'corrected')} · {t('tc_legend_click', 'click a day for its stamps')}
                </Typography>
            </Box>
        </Box>
    );
};

export default TimeClockOverviewTab;
